import { Popup } from 'components/common/popup/Popup';
import { alpha, Box, Stack, Tab, Tabs } from '@mui/material';
import { FormTabType } from 'components/common/form/tab';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import { FormLayoutContext, IFormLayoutContext } from 'components/common/form/layout/tab';
import FormTabsContent from 'components/common/form/layout/tab/FormTabsContent';
import { FormTabControlContext } from 'components/common/form/layout/tab/FormTabControlContext';
import { adjustDefaultPopupSettingsByType, getFormId } from 'components/common/form/formTools';

interface IFormAsidePanelLayoutProps extends Omit<
    IFormLayoutProps,
    'confirmPopupProps' | 'subFormPopup' | 'panelSettings' | 'sharedComponents'
> {}

const asidePanelLayoutContextValue: IFormLayoutContext = { formLayout: 'sidePanel', formType: 'edit' };

export default function FormAsidePanelLayout({
    elementProps,
    tabs,
    t,
    componentsMap,
    asPopupProps,
    hookForm,
    customTabs,
    customTabLabels,
    uid,
    isNew,
    entityType,
}: IFormAsidePanelLayoutProps) {
    const [activeTab, setActiveTab] = useState(0);
    const tabControl = useContext(FormTabControlContext);
    const onTabChange = (event: React.SyntheticEvent, value: number) => {
        setActiveTab(value);
        tabControl.onTabChanged?.(tabs.formTabs[value]?.name ?? '');
    };

    useEffect(() => {
        const requested = tabControl.requestedTab;
        if (!requested) {
            return;
        }
        const index = tabs.formTabs.findIndex((tab) => tab.name === requested.name);
        if (index >= 0) {
            setActiveTab(index);
        }
    }, [tabControl.requestedTab]);

    useEffect(() => {
        tabs.setFormTabs(
            tabs.formTabs.map((tab, index) => {
                tab.isActive = activeTab === index;
                return tab;
            }),
        );
    }, [activeTab]);

    const id = getFormId(uid, isNew);
    const submit = useRef(
        hookForm.form.hookFormHandleSubmit(function (data) {
            return hookForm.form.formAct.act.submit.exec(data);
        }),
    );

    return tabs.formTabs.length > 0 ? (
        <Popup
            settings={{
                ...adjustDefaultPopupSettingsByType(asPopupProps.popupSettings, asPopupProps.popupType, t, entityType),
                uid: 'p_' + uid,
            }}
            onHide={asPopupProps.onHide}
            onConfirm={submit.current}
            open={asPopupProps.isOpen}
            translationNS={elementProps.translationNS}
        >
            <Stack
                component={'form'}
                id={id}
                onSubmit={submit.current}
                sx={{
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                {!asPopupProps.popupSettings?.asidePanelHideTabs && (
                    <Box
                        sx={{
                            borderBottom: 1,
                            borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                            overflow: 'visible',
                            position: 'sticky',
                        }}
                    >
                        <Box sx={{ px: 3, flexShrink: 0 }}>
                            <Tabs value={activeTab} onChange={onTabChange}>
                                {tabs.formTabs.map((tab: FormTabType) => (
                                    <Tab label={customTabLabels?.[tab.name] ?? t(tab.title)} key={tab.name} />
                                ))}
                            </Tabs>
                        </Box>
                    </Box>
                )}
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <FormLayoutContext.Provider value={asidePanelLayoutContextValue}>
                        <FormTabsContent
                            componentsMap={componentsMap}
                            formTabs={tabs.formTabs}
                            elementProps={elementProps}
                            customTabs={customTabs}
                        />
                    </FormLayoutContext.Provider>
                </Box>
            </Stack>
        </Popup>
    ) : null;
}
